// C:\Proyectos\RentasIoT\src\app\pages\procedimientos\audioTrigger\audio-trigger.service.ts

import { Injectable, EventEmitter } from '@angular/core';

/**
 * Configuración del Audio Trigger
 */
export interface AudioTriggerConfig {
  umbral?: number; // Sensibilidad de detección (20-150). Por defecto: 120
  tiempoEspera?: number; // Milisegundos entre disparos (100-5000). Por defecto: 800
  smoothing?: number; // Suavizado de señal (0-1). Por defecto: 0.8
  usarDeteccionPico?: boolean; // Detectar solo picos abruptos (clics). Por defecto: true
  duracionMinimaPico?: number; // Duración mínima del pico en ms. Por defecto: 30
}

/**
 * Interface del servicio de Audio Trigger
 */
export interface IAudioTriggerService {
  onSenalDetectada: EventEmitter<void>;
  iniciar(config?: AudioTriggerConfig): Promise<boolean>;
  detener(): void;
  setDispositivo(deviceId: string): void;
  ajustarSensibilidad(nuevoUmbral: number): void;
  ajustarTiempoEspera(ms: number): void;
  isActive(): boolean;
}

/**
 * Servicio de Audio Trigger para detectar señales del equipo médico
 * Optimizado para grabaciones largas (15 min - 4 horas)
 * 🔥 MEJORADO: Detección de picos abruptos (clics) vs ruido continuo
 */
@Injectable({
  providedIn: 'root',
})
export class AudioTriggerService implements IAudioTriggerService {
  // EventEmitter para notificar cuando se detecta una señal
  public onSenalDetectada = new EventEmitter<void>();

  // Propiedades de Web Audio API
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private microphone: MediaStreamAudioSourceNode | null = null;
  private mediaStream: MediaStream | null = null;

  // Estado del servicio
  private detectando = false;
  private deviceId: string = 'default';

  // Configuración
  private umbral = 120; // 🔥 Aumentado por defecto
  private tiempoEsperaMs = 800; // 🔥 Aumentado por defecto
  private smoothing = 0.8; // 🔥 Mayor suavizado por defecto
  private usarDeteccionPico = true; // 🔥 NUEVO
  private duracionMinimaPico = 30; // 🔥 NUEVO: milisegundos

  // Control de cooldown
  private ultimaDeteccion = 0;
  private cooldownActivo = false;

  // 🔥 NUEVO: Para detección de picos
  private nivelAnterior = 0;
  private picoInicio = 0;
  private enPico = false;

  // Keep-alive para grabaciones largas
  private keepAliveInterval: any = null;

  constructor() {
    console.log('🔧 AudioTriggerService inicializado');
  }

  /**
   * Configura el dispositivo de audio a usar
   */
  setDispositivo(deviceId: string): void {
    this.deviceId = deviceId;
    console.log('✅ Dispositivo configurado:', deviceId.substring(0, 20) + '...');
  }

  /**
   * Inicia la detección de señales de audio
   */
  async iniciar(config?: AudioTriggerConfig): Promise<boolean> {
    try {
      // Aplicar configuración
      if (config) {
        if (config.umbral !== undefined) this.umbral = config.umbral;
        if (config.tiempoEspera !== undefined) this.tiempoEsperaMs = config.tiempoEspera;
        if (config.smoothing !== undefined) this.smoothing = config.smoothing;
        if (config.usarDeteccionPico !== undefined) this.usarDeteccionPico = config.usarDeteccionPico;
        if (config.duracionMinimaPico !== undefined) this.duracionMinimaPico = config.duracionMinimaPico;
      }

      console.log('🎤 Iniciando Audio Trigger (Modo Anti-Ruido)...');
      console.log(`   Dispositivo: ${this.deviceId === 'default' ? 'Por defecto' : 'Cable USB-Jack'}`);
      console.log(`   Umbral: ${this.umbral}`);
      console.log(`   Cooldown: ${this.tiempoEsperaMs}ms`);
      console.log(`   Smoothing: ${this.smoothing}`);
      console.log(`   Detección de pico: ${this.usarDeteccionPico ? 'SÍ' : 'NO'}`);
      if (this.usarDeteccionPico) {
        console.log(`   Duración mínima pico: ${this.duracionMinimaPico}ms`);
      }

      // Solicitar acceso al dispositivo de audio
      const constraints: MediaStreamConstraints = {
        audio:
          this.deviceId !== 'default'
            ? {
                deviceId: { exact: this.deviceId },
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
              }
            : {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
              },
      };

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Crear contexto de audio
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;
      this.analyser.smoothingTimeConstant = this.smoothing;

      // Conectar stream al analyser
      this.microphone = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.microphone.connect(this.analyser);

      // Iniciar detección
      this.detectando = true;

      if (this.usarDeteccionPico) {
        this.escucharPicos();
      } else {
        this.escucharSenales();
      }

      // Iniciar keep-alive para grabaciones largas
      this.iniciarKeepAlive();

      console.log('✅ Audio Trigger activado exitosamente');
      console.log('💡 CONSEJO: Si hay muchas fotos falsas, aumenta el umbral:');
      console.log('   audioTriggerDebug.ajustarUmbral(140)');
      return true;
    } catch (error) {
      console.error('❌ Error iniciando Audio Trigger:', error);
      return false;
    }
  }

  /**
   * Detiene la detección de señales
   */
  detener(): void {
    console.log('🛑 Deteniendo Audio Trigger...');

    this.detectando = false;

    // Detener keep-alive
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }

    // Desconectar y limpiar
    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
    this.cooldownActivo = false;
    this.ultimaDeteccion = 0;
    this.nivelAnterior = 0;
    this.picoInicio = 0;
    this.enPico = false;

    console.log('✅ Audio Trigger detenido');
  }

  /**
   * Sistema de keep-alive para mantener AudioContext activo durante grabaciones largas
   */
  private iniciarKeepAlive(): void {
    // Verificar cada 60 segundos que el AudioContext sigue activo
    this.keepAliveInterval = setInterval(() => {
      if (!this.audioContext) return;

      if (this.audioContext.state === 'suspended') {
        console.warn('⚠️ AudioContext suspendido, reactivando...');
        this.audioContext
          .resume()
          .then(() => {
            console.log('✅ AudioContext reactivado');
          })
          .catch((err) => {
            console.error('❌ Error reactivando AudioContext:', err);
          });
      } else if (this.audioContext.state === 'running') {
        console.log('💚 Audio Trigger activo (heartbeat)');
      }
    }, 60000); // Cada 60 segundos
  }

  /**
   * 🔥 MEJORADO: Detecta CAMBIOS ABRUPTOS (clics) vs ruido continuo
   * Usa el DELTA (velocidad de cambio) como criterio principal
   */
  private escucharPicos(): void {
    if (!this.detectando || !this.analyser) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    let frameCount = 0;
    const logInterval = 300;

    // 🔥 NUEVO: Historial de niveles para detectar cambios
    const historico: number[] = [];
    const HISTORIAL_MAX = 5; // Últimos 5 frames

    const analizar = () => {
      if (!this.detectando) return;

      this.analyser!.getByteFrequencyData(dataArray);

      // Calcular nivel actual
      const maximo = Math.max(...dataArray);
      const promedio = dataArray.reduce((a, b) => a + b, 0) / bufferLength;

      // 🔥 NUEVO: Calcular promedio de frecuencias medias-altas (clics mecánicos)
      // Los clics están típicamente entre 1-8kHz
      // Con fftSize=512, cada bin = ~86Hz (44100/512)
      // Bins 12-93 ≈ 1-8kHz
      const freqMediasAltas = dataArray.slice(12, 93);
      const promedioClics = freqMediasAltas.reduce((a, b) => a + b, 0) / freqMediasAltas.length;

      frameCount++;

      // Log periódico
      if (frameCount % logInterval === 0) {
        console.log(
          `🎵 Max=${maximo}, Avg=${promedio.toFixed(1)}, Clics=${promedioClics.toFixed(1)}, Umbral=${this.umbral}`,
        );
        frameCount = 0;
      }

      const ahora = Date.now();

      // Si está en cooldown, ignorar
      if (this.cooldownActivo) {
        requestAnimationFrame(analizar);
        return;
      }

      // 🔥 NUEVO: Calcular velocidad de cambio (delta)
      historico.push(maximo);
      if (historico.length > HISTORIAL_MAX) {
        historico.shift();
      }

      // Solo procesar si tenemos suficiente historial
      if (historico.length < 3) {
        requestAnimationFrame(analizar);
        return;
      }

      // Calcular delta (cambio respecto al promedio reciente)
      const promedioHistorico = historico.slice(0, -1).reduce((a, b) => a + b, 0) / (historico.length - 1);
      const delta = maximo - promedioHistorico;

      // 🔥 DETECCIÓN MEJORADA: Priorizar DELTA (cambio abrupto)
      const DELTA_MINIMO = 40; // Cambio abrupto mínimo (ajustable)
      const NIVEL_MINIMO = 50; // Nivel mínimo absoluto (más bajo que antes)

      // 1. Detectar INICIO de pico (cambio abrupto + nivel mínimo)
      if (!this.enPico && delta > DELTA_MINIMO && maximo > NIVEL_MINIMO) {
        this.enPico = true;
        this.picoInicio = ahora;
        console.log(
          `📈 Pico iniciado: Nivel=${maximo}, Delta=+${delta.toFixed(0)}, FreqClics=${promedioClics.toFixed(1)}`,
        );
      }

      // 2. Detectar FIN de pico (bajada)
      if (this.enPico && maximo < this.nivelAnterior - 15) {
        // Bajó al menos 15 puntos
        const duracionPico = ahora - this.picoInicio;

        // Verificar duración válida
        if (duracionPico >= this.duracionMinimaPico && duracionPico <= 200) {
          console.log(`🔊 ¡CLIC DETECTADO! Duración: ${duracionPico}ms, Nivel: ${maximo}, Delta: +${delta.toFixed(0)}`);

          // Activar cooldown
          this.cooldownActivo = true;
          this.ultimaDeteccion = ahora;

          // Emitir evento
          this.onSenalDetectada.emit();

          setTimeout(() => {
            this.cooldownActivo = false;
            console.log('✅ Cooldown terminado - Listo para nuevo clic');
          }, this.tiempoEsperaMs);
        } else {
          console.log(`⚠️ Pico descartado: Duración=${duracionPico}ms (esperado: ${this.duracionMinimaPico}-200ms)`);
        }

        this.enPico = false;
        this.picoInicio = 0;
        historico.length = 0; // Limpiar historial después de un pico
      }

      this.nivelAnterior = maximo;
      requestAnimationFrame(analizar);
    };

    analizar();
  }

  /**
   * Escucha y detecta señales de audio en tiempo real (modo original)
   */
  private escucharSenales(): void {
    if (!this.detectando || !this.analyser) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Contador para logging periódico
    let frameCount = 0;
    const logInterval = 300; // ~5 segundos a 60fps

    const analizar = () => {
      if (!this.detectando) return;

      this.analyser!.getByteFrequencyData(dataArray);

      // Calcular nivel de audio
      const maximo = Math.max(...dataArray);
      const promedio = dataArray.reduce((a, b) => a + b, 0) / bufferLength;

      frameCount++;

      // Log periódico para monitoreo (cada 5 segundos)
      if (frameCount % logInterval === 0) {
        console.log(`🎵 Nivel de audio: Max=${maximo}, Avg=${promedio.toFixed(1)}, Umbral=${this.umbral}`);
        frameCount = 0;
      }

      const ahora = Date.now();

      // Si está en cooldown, ignorar señales
      if (this.cooldownActivo) {
        requestAnimationFrame(analizar);
        return;
      }

      // Detectar pico de señal
      if (maximo > this.umbral) {
        const tiempoDesdeUltima = ahora - this.ultimaDeteccion;

        // Solo disparar si ha pasado suficiente tiempo
        if (tiempoDesdeUltima >= this.tiempoEsperaMs) {
          console.log(`🔊 ¡SEÑAL DETECTADA! Nivel: ${maximo} (umbral: ${this.umbral})`);
          console.log(`   Tiempo desde última foto: ${tiempoDesdeUltima}ms`);

          // Activar cooldown
          this.cooldownActivo = true;
          this.ultimaDeteccion = ahora;

          // Emitir evento
          this.onSenalDetectada.emit();

          // Desactivar cooldown después del tiempo configurado
          setTimeout(() => {
            this.cooldownActivo = false;
            console.log('✅ Cooldown terminado - Listo para nueva foto');
          }, this.tiempoEsperaMs);
        } else {
          // Señal detectada pero aún en cooldown (log discreto)
          if (frameCount % 60 === 0) {
            // Cada segundo aprox
            const falta = this.tiempoEsperaMs - tiempoDesdeUltima;
            console.log(`⏳ Señal en cooldown (${falta}ms restantes)`);
          }
        }
      }

      requestAnimationFrame(analizar);
    };

    analizar();
  }

  /**
   * Ajusta la sensibilidad de detección en tiempo real
   */
  ajustarSensibilidad(nuevoUmbral: number): void {
    if (nuevoUmbral < 20) {
      console.warn('⚠️ Umbral muy bajo, mínimo 20');
      this.umbral = 20;
    } else if (nuevoUmbral > 200) {
      console.warn('⚠️ Umbral muy alto, máximo 200');
      this.umbral = 200;
    } else {
      this.umbral = nuevoUmbral;
      console.log(`✅ Sensibilidad ajustada: ${this.umbral}`);
      console.log(`   Menos sensible → valor más alto (ej: 150)`);
      console.log(`   Más sensible → valor más bajo (ej: 80)`);
    }
  }

  /**
   * Ajusta el tiempo de espera entre disparos en tiempo real
   */
  ajustarTiempoEspera(ms: number): void {
    if (ms < 100) {
      console.warn('⚠️ Tiempo de espera muy corto, mínimo 100ms');
      this.tiempoEsperaMs = 100;
    } else if (ms > 5000) {
      console.warn('⚠️ Tiempo de espera muy largo, máximo 5000ms');
      this.tiempoEsperaMs = 5000;
    } else {
      this.tiempoEsperaMs = ms;
      console.log(`✅ Tiempo de espera ajustado: ${ms}ms`);
    }
  }

  /**
   * Verifica si el servicio está activo
   */
  isActive(): boolean {
    return this.detectando;
  }
}
